import React from 'react';

function Privacy() {
  return (
    <div className="max-w-7xl mx-auto px-4 py-16 text-gray-700">
      <h1 className="text-3xl font-bold text-gray-800 mb-6">Privacy Policy</h1>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">1. Introduction</h2>
        <p className="leading-relaxed">
          Welcome to the privacy policy of SALINAS TRADING LLC. We respect your privacy and are committed to protecting your personal data. This privacy policy explains how we collect, process, and safeguard your personal data when you visit and use our website, regardless of your location. It also outlines your privacy rights and how the law protects you. By using this website, you signify your acceptance of this privacy policy.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">2. Important Information</h2>
        <p className="leading-relaxed">
          This website is intended for users aged 18 and above, and we do not knowingly collect data relating to children. Please read this privacy policy alongside any other privacy or fair processing notices we may provide when collecting or processing your personal data, so you are fully aware of how and why we use your data. This policy supplements other notices and does not override them.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">3. Who We Are and How to Contact Us</h2>
        <p className="leading-relaxed">
          SALINAS TRADING LLC, a Dubai-based company, is the controller responsible for the collection and use of your personal data (“we”, “us”, or “our”). If you have questions about this privacy policy or wish to exercise your legal rights, please contact our data privacy manager at <a href="mailto:privacy@salinastrading.com" className="text-blue-600 hover:underline">privacy@salinastrading.com</a>.
        </p>
        <p className="leading-relaxed mt-2">
          You have the right to make a complaint to the UAE Data Office, established under Federal Decree-Law No. 44 of 2021, the UAE regulator for data protection issues. We would appreciate the opportunity to address your concerns first, so please contact us before approaching the regulator.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">4. Changes to This Privacy Policy and Your Information</h2>
        <p className="leading-relaxed">
          We may update this privacy policy by posting a new version on our website. Changes will not apply retroactively and will take effect from the posting date. Please check the date at the bottom of this policy to stay informed of updates. Historic versions can be obtained by contacting us at <a href="mailto:privacy@salinastrading.com" className="text-blue-600 hover:underline">privacy@salinastrading.com</a>.
        </p>
        <p className="leading-relaxed mt-2">
          Please keep us informed if your personal data changes during your relationship with us to ensure our records remain accurate.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">5. Third-Party Links</h2>
        <p className="leading-relaxed">
          Our website may include links to third-party websites, plug-ins, or applications. Clicking these links may allow third parties to collect or share your data. We do not control these websites and are not responsible for their privacy policies. We encourage you to read the privacy policy of every website you visit.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">6. The Data We Collect About You</h2>
        <p className="leading-relaxed">
          When you access our website, we may exchange information with your device, including personal data, to fulfill contracts, optimize our website, or display relevant advertising. Personal data means any information that can identify you, excluding anonymous data.
        </p>
        <p className="leading-relaxed mt-2">We may collect, use, store, and transfer the following types of personal data:</p>
        <ul className="list-disc pl-6 mt-2">
          <li><strong>Identity Data</strong>: First name, last name, username (if you create an account), date of birth, age, gender.</li>
          <li><strong>Contact Data</strong>: Email address, telephone numbers.</li>
          <li><strong>Financial Data</strong>: Bank account and payment card details (for online sales).</li>
          <li><strong>Technical Data</strong>: IP address, login data, browser type/version, time zone, location, operating system, and device technology.</li>
          <li><strong>Profile Data</strong>: Username, password, interests, preferences, feedback, survey responses.</li>
          <li><strong>Usage Data</strong>: Information about how you use our website, products, and services.</li>
          <li><strong>Marketing and Communications Data</strong>: Preferences for receiving marketing and communication preferences.</li>
        </ul>
        <p className="leading-relaxed mt-2">
          We also collect Aggregated Data (e.g., statistical or demographic data) for purposes like analyzing website usage. This data is not personal data unless combined with identifiable information, in which case we treat it as personal data.
        </p>
        <p className="leading-relaxed mt-2">
          We do not collect Sensitive Personal Data (e.g., race, religion, political opinions, health, or biometrics). If you fail to provide required personal data, we may be unable to perform contracts (e.g., deliver goods), and we will notify you if this occurs.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">7. How Is Your Personal Data Collected?</h2>
        <p className="leading-relaxed">We collect data through:</p>
        <ul className="list-disc pl-6 mt-2">
          <li><strong>Direct Interactions</strong>: Providing Identity, Contact, or Financial Data via forms, emails, or when requesting products, creating accounts, subscribing, or giving feedback.</li>
          <li><strong>Automated Technologies</strong>: Collecting Technical Data via cookies, server logs, or similar technologies. See our cookie policy for details.</li>
          <li><strong>Third Parties</strong>: Receiving Technical, Contact, or Financial Data from analytics providers (e.g., Google) or payment/delivery services.</li>
        </ul>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">8. How We Use Your Personal Data</h2>
        <p className="leading-relaxed">
          We use your personal data only when legally permitted, primarily to perform contracts or comply with legal obligations. We may seek your consent for marketing communications, which you can withdraw at <a href="mailto:privacy@salinastrading.com" className="text-blue-600 hover:underline">privacy@salinastrading.com</a>.
        </p>
        <p className="leading-relaxed mt-2">
          The following table outlines the purposes for which we use your personal data and the legal bases we rely on:
        </p>
        <div className="overflow-x-auto mt-4">
          <table className="w-full border-collapse border border-gray-300">
            <thead>
              <tr className="bg-gray-100">
                <th className="border border-gray-300 p-3 text-left font-semibold text-gray-800">Purpose/Activity</th>
                <th className="border border-gray-300 p-3 text-left font-semibold text-gray-800">Type of Data</th>
                <th className="border border-gray-300 p-3 text-left font-semibold text-gray-800">Lawful Basis for Processing</th>
              </tr>
            </thead>
            <tbody>
              <tr className="bg-white">
                <td className="border border-gray-300 p-3">Register you as a new customer</td>
                <td className="border border-gray-300 p-3">Identity, Contact</td>
                <td className="border border-gray-300 p-3">Performance of a contract with you</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 p-3">Process and deliver your order, including managing payments, fees, and charges; collect and recover money owed to us</td>
                <td className="border border-gray-300 p-3">Identity, Contact, Financial, Transaction, Marketing and Communications</td>
                <td className="border border-gray-300 p-3">Performance of a contract with you</td>
              </tr>
              <tr className="bg-white">
                <td className="border border-gray-300 p-3">Manage our relationship with you, including notifying you about changes to our terms or privacy policy; asking you to leave a review or take a survey</td>
                <td className="border border-gray-300 p-3">Identity, Contact, Profile, Marketing and Communications</td>
                <td className="border border-gray-300 p-3">Performance of a contract with you; Necessary to comply with a legal obligation</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 p-3">Enable you to take part in a prize draw, competition, or complete a survey</td>
                <td className="border border-gray-300 p-3">Identity, Contact, Profile, Usage, Marketing and Communications</td>
                <td className="border border-gray-300 p-3">Performance of a contract with you</td>
              </tr>
              <tr className="bg-white">
                <td className="border border-gray-300 p-3">Administer and protect our business and this website (including troubleshooting, data analysis, testing, system maintenance, support, reporting, and hosting of data)</td>
                <td className="border border-gray-300 p-3">Identity, Contact, Technical</td>
                <td className="border border-gray-300 p-3">Necessary to comply with a legal obligation</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 p-3">Deliver relevant website content and advertisements to you and measure or understand the effectiveness of the advertising we serve to you</td>
                <td className="border border-gray-300 p-3">Identity, Contact, Profile, Usage, Marketing and Communications, Technical</td>
                <td className="border border-gray-300 p-3">Your consent</td>
              </tr>
              <tr className="bg-white">
                <td className="border border-gray-300 p-3">Use data analytics to improve our website, products/services, marketing, customer relationships, and experiences</td>
                <td className="border border-gray-300 p-3">Technical, Usage</td>
                <td className="border border-gray-300 p-3">Your consent</td>
              </tr>
              <tr className="bg-gray-50">
                <td className="border border-gray-300 p-3">Make suggestions and recommendations to you about goods or services that may be of interest to you</td>
                <td className="border border-gray-300 p-3">Identity, Contact, Technical, Usage, Profile, Marketing and Communications</td>
                <td className="border border-gray-300 p-3">Your consent</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">9. Marketing/Promotional Offers</h2>
        <p className="leading-relaxed">
          We may use your data to tailor marketing based on your preferences. You’ll receive marketing if you’ve consented, requested information, or purchased from us without opting out. We require opt-in consent for third-party marketing. Opt out anytime via links in marketing emails or by emailing <a href="mailto:privacy@salinastrading.com" className="text-blue-600 hover:underline">privacy@salinastrading.com</a>.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">10. Cookies</h2>
        <p className="leading-relaxed">
          Cookies are small files stored on your device to enhance your website experience. You can adjust browser settings to refuse cookies, but this may affect functionality. See our cookie policy for details.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">11. Sharing Your Personal Data</h2>
        <p className="leading-relaxed">
          We may share your data with group companies, service providers (e.g., IT, payment gateways), professional advisers, regulators, or third parties during business transfers. All third parties are required to secure your data and process it only per our instructions. Contact <a href="mailto:privacy@salinastrading.com" className="text-blue-600 hover:underline">privacy@salinastrading.com</a> for details on our service partners.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">12. International Transfers</h2>
        <p className="leading-relaxed">
          Your data may be transferred outside the UAE within our group or to third parties. We ensure protection through adequate country standards or contractual safeguards. Contact us for details on transfer mechanisms.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">13. Data Security</h2>
        <p className="leading-relaxed">
          We use appropriate security measures to prevent unauthorized access, loss, or disclosure of your data. Access is limited to authorized personnel, and we have procedures to address data breaches, notifying you and regulators as required.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">14. Data Retention</h2>
        <p className="leading-relaxed">
          We retain your data only as long as necessary for its collection purposes, reviewing it every 2 years. Inactive users’ data is deleted after this period unless required for legal or regulatory purposes. Contact <a href="mailto:privacy@salinastrading.com" className="text-blue-600 hover:underline">privacy@salinastrading.com</a> for retention details.
        </p>
      </section>

      <section className="mb-8">
        <h2 className="text-2xl font-semibold mb-4">15. Your Legal Rights</h2>
        <p className="leading-relaxed">
          You have rights to access, correct, delete, object to, restrict, or transfer your personal data. Exercise these rights by emailing <a href="mailto:privacy@salinastrading.com" className="text-blue-600 hover:underline">privacy@salinastrading.com</a>. We may verify your identity and may charge a fee for unfounded or excessive requests. You can also contact the UAE Data Office or our data protection officer.
        </p>
      </section>

      <p className="text-sm text-gray-500">Last updated: April 18, 2025</p>
    </div>
  );
}

export default Privacy;